import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Loader2, Check } from "lucide-react";

const TIMEZONES = ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Berlin", "Asia/Singapore", "Asia/Tokyo"];
const COUNTRIES = ["US", "CA", "GB", "DE", "FR", "NL", "AU", "SG", "JP", "IN"];

export default function Signup() {
  const [f, setF] = useState({ first_name: "", last_name: "", company: "", email: "", password: "", country: "US", timezone: "UTC" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", f);
      login(data.token, data.user);
      nav("/app/deploy");
    } catch (e) { setErr(apiErr(e)); setBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#06080D]">
      <div className="hidden w-1/2 flex-col justify-between border-r border-slate-800 p-12 lg:flex zk-grid-bg">
        <Logo />
        <div>
          <h2 className="font-display text-3xl font-bold text-slate-50">Deploy your first offsite backup server in minutes.</h2>
          <ul className="mt-6 space-y-3">
            {["No hardware, no second site", "Native Proxmox workflow", "Verified, monitored backups", "Cancel any time"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-slate-300"><Check className="h-4 w-4 text-emerald-400" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-slate-600">ZettaKeep — Offsite Proxmox backups in minutes.</div>
      </div>
      <div className="flex w-full items-center justify-center overflow-y-auto p-6 lg:w-1/2">
        <div className="w-full max-w-md py-8">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display text-2xl font-bold text-slate-50">Create your account</h1>
          <p className="mt-1 text-sm text-slate-400">Start protecting your Proxmox VE offsite.</p>
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="signup-form">
            <div className="grid grid-cols-2 gap-3">
              <Input required placeholder="First name" value={f.first_name} onChange={set("first_name")} data-testid="signup-first" className="bg-slate-950 border-slate-700" />
              <Input required placeholder="Last name" value={f.last_name} onChange={set("last_name")} data-testid="signup-last" className="bg-slate-950 border-slate-700" />
            </div>
            <Input required placeholder="Company" value={f.company} onChange={set("company")} data-testid="signup-company" className="bg-slate-950 border-slate-700" />
            <Input required type="email" placeholder="Work email" value={f.email} onChange={set("email")} data-testid="signup-email" className="bg-slate-950 border-slate-700" />
            <Input required type="password" placeholder="Password (min 8 chars)" value={f.password} onChange={set("password")} data-testid="signup-password" className="bg-slate-950 border-slate-700" />
            <div className="grid grid-cols-2 gap-3">
              <select value={f.country} onChange={set("country")} data-testid="signup-country" className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={f.timezone} onChange={set("timezone")} data-testid="signup-timezone" className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {err && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="signup-error">{err}</div>}
            <button disabled={busy} data-testid="signup-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-400 py-2.5 font-semibold text-[#06080D] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
            </button>
          </form>
          <p className="mt-5 text-sm text-slate-500">Already have an account? <Link to="/login" className="text-cyan-300 hover:underline">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
